import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import {
  PageSurface,
  PageWrapper,
} from "../../../components/layout/container/Container";

import { PageHeader } from "../../../components/layout/header/Header";
import { Heading } from "../../../components/common/heading/Heading";
import SecondaryPillTabs from "../../../components/tabs/SecondaryPillTabs";
import EmptyState from "../../../components/feedback/EmptyState";

import ChannelVideosView from "../../videos/channels/views/ChannelVideosView";
import ChannelPopsView from "../../pops/channels/views/ChannelPopsView";
import ChannelAlbumsView from "../../albums/channels/views/ChannelAlbumsView";
import ChannelStreamsView from "../../streams/channels/views/ChannelStreamsView";

import ChannelFollowButton from "../components/ChannelFollowButton";
import { useChannelProfile } from "../hooks/useChannelProfile";

import "./ChannelPage.css";

const PREVIEW_LIMIT = 5;
const PAGE_LIMIT = 12;

const CHANNEL_TABS = [
  { id: "home", label: "Home" },
  { id: "videos", label: "Videos" },
  { id: "albums", label: "Albums" },
  {
    id: "more",
    label: "More",
    menu: [
      { id: "pops", label: "Pops" },
      { id: "streams", label: "Streams" },
    ],
  },
];

const VALID_TABS = ["home", "videos", "albums", "pops", "streams"];

const CHANNEL_VIEWS = {
  videos: ChannelVideosView,
  albums: ChannelAlbumsView,
  pops: ChannelPopsView,
  streams: ChannelStreamsView,
};

const getInitial = (name) =>
  String(name || "C")
    .trim()
    .charAt(0)
    .toUpperCase();

function ChannelMediaView({ type, username, preview = false }) {
  const Component = CHANNEL_VIEWS[type];

  if (!Component) return null;

  return (
    <Component
      username={username}
      preview={preview}
      limit={preview ? PREVIEW_LIMIT : PAGE_LIMIT}
    />
  );
}

export default function ChannelPage() {
  const { creatorusername = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const username = String(creatorusername || "").trim();

  const {
    creator,
    loading: loadingProfile,
    error,
  } = useChannelProfile(username);

  const [avatarFailed, setAvatarFailed] = useState(false);

  const requestedTab = searchParams.get("tab");
  const activeTab = VALID_TABS.includes(requestedTab) ? requestedTab : "home";

  const brandName = String(
    creator?.brandName || creator?.brand_name || "",
  ).trim();

  const channelTitle = brandName || username || "Channel";

  const avatarUrl = creator?.avatarUrl || creator?.profileImageUrl || null;

  useEffect(() => {
    setAvatarFailed(false);
  }, [username, avatarUrl]);

  const handleTabChange = (selectedTab) => {
    const nextTab =
      typeof selectedTab === "string"
        ? selectedTab
        : selectedTab?.id || selectedTab?.value || selectedTab?.path;

    if (!VALID_TABS.includes(nextTab)) return;
    if (nextTab === activeTab) return;

    setSearchParams(nextTab === "home" ? {} : { tab: nextTab });
  };

  const renderContent = () => {
    if (activeTab === "home") {
      return (
        <>
          {["videos", "pops", "albums", "streams"].map((type) => (
            <ChannelMediaView
              key={`${username}-${type}`}
              type={type}
              username={username}
              preview
            />
          ))}
        </>
      );
    }

    return (
      <ChannelMediaView
        key={`${username}-${activeTab}`}
        type={activeTab}
        username={username}
      />
    );
  };

  if (!username) {
    return (
      <PageSurface
        className="katch-channel"
        backgroundColor="var(--ko-bg-dark-1)"
      >
        <PageWrapper className="katch-channel__container">
          <EmptyState
            title="Creator unavailable"
            description="Creator username is required."
          />

          <Link to="/" className="katch-channel__back">
            Back to Katch
          </Link>
        </PageWrapper>
      </PageSurface>
    );
  }

  return (
    <PageSurface>
      <PageWrapper>
        {error && !creator && !loadingProfile ? (
          <div className="katch-channel__empty">
            <EmptyState title="Channel unavailable" description={error} />

            <Link to="/" className="katch-channel__back">
              Back to Katch
            </Link>
          </div>
        ) : (
          <>
            <PageHeader>
              <div className="katch-channel__profile">
                <div className="katch-channel__avatar">
                  {avatarUrl && !avatarFailed ? (
                    <img
                      src={avatarUrl}
                      alt={channelTitle}
                      onError={() => setAvatarFailed(true)}
                    />
                  ) : (
                    <span>{getInitial(brandName || username)}</span>
                  )}
                </div>

                <div className="katch-channel__profile-info">
                  <Heading
                    title={loadingProfile ? "Loading channel..." : channelTitle}
                    subTitle={`@${username}`}
                    size="xl"
                  />

                  {!loadingProfile && creator && (
                    <div className="katch-channel__actions">
                      <ChannelFollowButton username={username} />
                    </div>
                  )}
                </div>
              </div>
            </PageHeader>

            <div
              className="katch-channel__navigation"
              style={{ marginBottom: "2rem" }}
            >
              <SecondaryPillTabs
                tabs={CHANNEL_TABS}
                activeTab={activeTab}
                onChange={handleTabChange}
              />
            </div>

            <div className="katch-channel__content">{renderContent()}</div>
          </>
        )}
      </PageWrapper>
    </PageSurface>
  );
}
